import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import ExpandedIcon from "../assets/doc-ref-1.png";

function DocumentReferenceCard({
    title,
    inContext,
    documentData,
    fhirClient,
    patientId,
    enableSearch = false,
    onDocumentChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalDocumentDetails, setModalDocumentDetails] = useState(null);
    const [authorName, setAuthorName] = useState("Loading...");
    const [documentHistory, setDocumentHistory] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(documentData || null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const handleModalClose = () => setShowModal(false);

    /** INITIAL DATA SYNC */
    useEffect(() => {
        if (
            documentData &&
            typeof documentData === "object" &&
            "error" in documentData &&
            typeof documentData.error === "number"
        ) {
            setError(`Error loading document (code: ${documentData.error}).`);
            setSelectedDocument(null);
        } else {
            setError("");
            setSelectedDocument(documentData || null);
        }
    }, [documentData]);

    /** FETCH AUTHOR (similar to performer in Observation) */
    useEffect(() => {
        const fetchAuthor = async () => {
            const ref = selectedDocument?.author?.[0]?.reference;
            if (!fhirClient || !ref || !ref.includes("/")) {
                setAuthorName("N/A");
                return;
            }
            try {
                const data = await fhirClient.request(ref);
                setAuthorName(data?.name || data?.display || "Unknown Author");
            } catch (err) {
                console.error("Failed to fetch author details", err);
                setAuthorName("N/A");
            }
        };

        if (selectedDocument) fetchAuthor();
        else setAuthorName("N/A");
    }, [selectedDocument, fhirClient]);

    /** FETCH DOCUMENT HISTORY */
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchDocuments = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `DocumentReference?patient=Patient/${patientId}&_sort=-date&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setDocumentHistory(found);
            } catch (err) {
                console.error("Error fetching document history:", err);
                setDocumentHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchDocuments();
    }, [enableSearch, patientId, fhirClient]);

    /** AUTO SELECT LATEST */
    useEffect(() => {
        if (!isReply && enableSearch) {
            if (documentHistory.length > 0) {
                const firstDoc = documentHistory[0];
                setSelectedDocument(firstDoc);
                if (onDocumentChange) onDocumentChange(firstDoc);
            } else {
                setSelectedDocument(null);
                if (onDocumentChange) onDocumentChange(null);
            }
        }
    }, [documentHistory]);

    /** OPEN MODAL */
    const handleModalOpen = async () => {
        if (!selectedDocument || !selectedDocument.id) return;

        try {
            const data = await fhirClient.request(`DocumentReference/${selectedDocument.id}`);
            setModalDocumentDetails(data);
            setShowModal(true);
        } catch (err) {
            console.error("Failed to fetch document details", err);
            setModalDocumentDetails(null);
            setShowModal(true);
        }
    };

    const handleSelectDocument = (doc) => {
        setSelectedDocument(doc);
        if (onDocumentChange) onDocumentChange(doc);
    };

    /** FIELD RENDERERS (DocumentReference R4) */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="type">
            <strong>Type:</strong> {selectedDocument?.type?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="status">
            <strong>Status:</strong> {selectedDocument?.status || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="author">
            <strong>Author:</strong> {authorName}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="date">
            <strong>Date:</strong> {formatLocalDate(selectedDocument?.date)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="category">
            <strong>Category:</strong>{" "}
            {selectedDocument?.category?.map((c) => c.text).join(", ") || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="contentCount">
            <strong>Content Attachments:</strong>{" "}
            {selectedDocument?.content?.length || 0}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                <Card.Header
                    onClick={handleModalOpen}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleModalOpen();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader} ${styles.clickableHeader}`}
                >
                    {/* Left side: Icon + Title */}
                    <div className="d-flex align-items-center">
                        <img
                            src={ExpandedIcon}
                            alt="Document Icon"
                            className={styles.fhirIcon}
                        // optional: keep icon click separate or inherit header click
                        />
                        <h6 className="mb-0" style={{ cursor: "pointer" }}>
                            {title?.length > 75
                                ? `Document: ${title.substring(0, 75)}…`
                                : `Document: ${title}`}
                        </h6>
                    </div>

                    {/* Right side: Dropdown + controls */}
                    <div
                        className="d-flex align-items-center"
                        onClick={(e) => e.stopPropagation()} // only prevent dropdown clicks from triggering header
                    >
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectDocument(
                                            documentHistory.find((d) => d.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Document
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        {documentHistory.map((doc) => (
                                            <Dropdown.Item key={doc.id} eventKey={doc.id}>
                                                {doc.type?.text || "Document"} · {formatLocalDate(doc.date)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>


                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalDocumentDetails}
                //title={selectedDocument?.type?.text || "Document"}
                fhirClient={fhirClient}
                //icon={ExpandedIcon}
            />
        </>
    );
}

export default DocumentReferenceCard;
